import React, { useCallback, useContext } from "react";
import {
  type BlockerFunction,
  matchPath,
  NavigationType,
  UNSAFE_DataRouterContext,
  useBlocker,
} from "react-router-dom";
import ConfirmationDialog from "../../components/ConfirmationDialog";

export function DiscardChangesPrompt({
  open,
  onKeepEditing,
  onDiscard,
}: Readonly<{ open: boolean; onKeepEditing: () => void; onDiscard: () => void }>) {
  return (
    <ConfirmationDialog
      open={open}
      onClose={onKeepEditing}
      onConfirm={onDiscard}
      title="Discard changes?"
      description="You have unsaved changes. If you leave, they will be lost."
      confirmText="Discard"
      cancelText="Keep editing"
      maxWidth="xs"
      destructive
    >
      {null}
    </ConfirmationDialog>
  );
}

interface DirtyDialogBlockerProps {
  pattern: string;
  end: boolean;
  bypassRef: React.MutableRefObject<boolean>;
}

function DataRouterBlocker({ pattern, end, bypassRef }: Readonly<DirtyDialogBlockerProps>) {
  const shouldBlock = useCallback<BlockerFunction>(
    ({ nextLocation, historyAction }) =>
      historyAction === NavigationType.Pop &&
      !bypassRef.current &&
      !matchPath({ path: pattern, end }, nextLocation.pathname),
    [pattern, end, bypassRef],
  );
  const blocker = useBlocker(shouldBlock);

  return (
    <DiscardChangesPrompt
      open={blocker.state === "blocked"}
      onKeepEditing={() => blocker.reset?.()}
      onDiscard={() => {
        bypassRef.current = true;
        blocker.proceed?.();
      }}
    />
  );
}

/**
 * Blocks Back before it leaves a dirty dialog, so the page or drawer that owns the
 * dialog (and its form state) never unmounts. `useBlocker` needs a data router
 * (`createBrowserRouter`); under `<BrowserRouter>` this renders nothing and the dialog
 * relies on its restore-after-Back fallback.
 */
export function DirtyDialogBlocker(props: Readonly<DirtyDialogBlockerProps>) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  return dataRouter ? <DataRouterBlocker {...props} /> : null;
}
